import {
  getInstalledCfdPushBridge,
  installCfdPushBridge,
  isCfdPushAppWebView,
  type CfdPushPlatform,
  type CfdPushMessageHandler,
  type CfdPushMessageUnsubscribe,
  type CfdPushResult,
  type CfdPushState,
} from './cfdPushBridge'
import { useCfdPushStore } from './cfdPushStore'

export const CfdPush = {
  install: installCfdPushBridge,
  platform: (): CfdPushPlatform => getInstalledCfdPushBridge()?.platform() ?? 'web',
  isApp: (): boolean => !!getInstalledCfdPushBridge()?.isApp(),
  isAndroid: (): boolean => !!getInstalledCfdPushBridge()?.isAndroid(),
  isIOS: (): boolean => !!getInstalledCfdPushBridge()?.isIOS(),
  isAppWebView: isCfdPushAppWebView,

  getSession: (): string => useCfdPushStore.getState().sessionToken,

  setSession: (accessToken?: string | null): Promise<CfdPushResult> => {
    const token = accessToken ?? ''
    if (!token) {
      useCfdPushStore.getState().clearSessionToken()
      return CfdPush.clearSession()
    }

    const bridge = getInstalledCfdPushBridge()
    if (!bridge?.isApp()) {
      return Promise.resolve({ ok: false, httpCode: 0, data: { message: 'not app' } })
    }

    useCfdPushStore.getState().setSessionToken(token)
    return bridge.setSession(token)
  },

  restoreSession: (): Promise<CfdPushResult> => {
    if (!CfdPush.isApp()) {
      return Promise.resolve({ ok: false, httpCode: 0, data: { message: 'not app' } })
    }

    const token = useCfdPushStore.getState().sessionToken
    if (!token) {
      return Promise.resolve({ ok: true, httpCode: 0, data: { message: 'no session' } })
    }

    return CfdPush.setSession(token)
  },

  clearSession: (): Promise<CfdPushResult> => {
    useCfdPushStore.getState().clearSessionToken()
    return (
      getInstalledCfdPushBridge()?.clearSession() ??
      Promise.resolve({ ok: false, httpCode: 0, data: { message: 'not app' } })
    )
  },

  setEnabled: (enabled: boolean): Promise<CfdPushResult> => {
    useCfdPushStore.getState().setPushEnabled(enabled)
    return (
      getInstalledCfdPushBridge()?.setEnabled(enabled) ??
      Promise.resolve({ ok: false, httpCode: 0, data: { message: 'not app' } })
    )
  },

  register: (): Promise<CfdPushResult> =>
    getInstalledCfdPushBridge()?.register() ??
    Promise.resolve({ ok: false, httpCode: 0, data: { message: 'not app' } }),

  state: (): Promise<CfdPushState | null> =>
    getInstalledCfdPushBridge()?.state() ?? Promise.resolve(null),

  requestPermission: () => {
    getInstalledCfdPushBridge()?.requestPermission()
  },

  onPushMessage: (handler: CfdPushMessageHandler): CfdPushMessageUnsubscribe => {
    return getInstalledCfdPushBridge()?.onPushMessage(handler) ?? (() => undefined)
  },
}
